import { useRef, useState } from "react";
import { ArrowUp, Paperclip, X } from "lucide-react";

const CLIPBOARD_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export function getClipboardAttachmentFile(clipboardData) {
  const items = Array.from(clipboardData?.items || []);
  for (const item of items) {
    if (item?.kind !== "file" || !item.type?.startsWith("image/")) continue;
    const file = item.getAsFile?.();
    if (file && CLIPBOARD_IMAGE_TYPES.has(file.type)) return file;
  }
  return null;
}

export default function SupportComposer({
  placeholder = "Message...",
  disabled = false,
  sending = false,
  uploading = false,
  error = "",
  attachment = null,
  onAttach,
  onClearAttachment,
  onSend,
}) {
  const [body, setBody] = useState("");
  const fileRef = useRef(null);
  const sendDisabled = disabled || sending || uploading || (!body.trim() && !attachment);

  async function handleSubmit(event) {
    event.preventDefault();
    if (sendDisabled) return;
    await onSend?.(body.trim());
    setBody("");
  }

  function handlePaste(event) {
    const file = getClipboardAttachmentFile(event.clipboardData);
    if (!file) return;
    event.preventDefault();
    onAttach?.(file);
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-slate-200 bg-white p-3">
      {attachment ? (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-700">
          <span className="truncate">{attachment.name}</span>
          <button type="button" onClick={onClearAttachment} className="rounded p-1 text-slate-500 hover:bg-white hover:text-slate-900" aria-label="Remove attachment">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
      {uploading ? (
        <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full w-2/3 animate-pulse rounded-full bg-teal-600" />
        </div>
      ) : null}
      {error ? (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-700">
          {error}
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onPaste={handlePaste}
          disabled={disabled || sending}
          rows={1}
          maxLength={4000}
          placeholder={placeholder}
          className="max-h-24 min-h-10 flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-950 shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        />
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(event) => onAttach?.(event.target.files?.[0] || null)}
        />
        <button
          type="button"
          disabled={disabled || sending || uploading}
          onClick={() => fileRef.current?.click()}
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Attach file"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <button
          type="submit"
          disabled={sendDisabled}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-teal-700 text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Send message"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
